import { ReactNode } from 'react';
import { DataTestId, LocalizedCurrencySimpleValue, SimpleAmount, TextKey } from '@mews-distributor/common';
import { LocalizedText } from '@mews/framework';
import { Body, Highlight, Stack, Typography } from '@optimus-web/core';

interface TotalBarWithDccViewProps {
    totalPrice: SimpleAmount;
    hasNetPricing: boolean;
    hasSurchargeService: boolean;
    surchargeFees: {
        visa: number;
        mastercard: number;
        amex: number;
    };
    renderRateGroupDescription?: () => ReactNode;
    selectedCurrency: string | undefined;
    paymentGatewayCurrency: string;
    totalAmountInPaymentGatewayCurrency: SimpleAmount;
    showSurchargingMessage: boolean;
    isImmediatelySettledTotalAmountPositive: boolean;
    showDccFeeMessage?: boolean;
}

export const TotalBarWithDccView = (props: TotalBarWithDccViewProps) => {
    const {
        totalPrice,
        hasNetPricing,
        hasSurchargeService,
        surchargeFees,
        renderRateGroupDescription,
        selectedCurrency,
        paymentGatewayCurrency,
        totalAmountInPaymentGatewayCurrency,
        showSurchargingMessage,
        isImmediatelySettledTotalAmountPositive,
        showDccFeeMessage = false,
        ...other
    } = props;

    return (
        <Stack spacing="100" vertical {...other}>
            <Stack spacing="200" distribution="space-between">
                <Highlight size="medium" data-test-id={DataTestId.TotalBarTotal}>
                    <LocalizedText textKey={TextKey.ReservationSummaryTotalBarCardTotalAmount} />
                </Highlight>
                <Highlight size="medium">
                    {paymentGatewayCurrency !== selectedCurrency && isImmediatelySettledTotalAmountPositive ? (
                        <LocalizedCurrencySimpleValue
                            value={totalPrice}
                            data-test-id={DataTestId.TotalBarTotalValueSelectedCurrency}
                        />
                    ) : (
                        <LocalizedCurrencySimpleValue
                            value={totalAmountInPaymentGatewayCurrency}
                            data-test-id={DataTestId.TotalBarTotalValuePaymentGateway}
                        />
                    )}
                </Highlight>
            </Stack>
            {paymentGatewayCurrency !== selectedCurrency ? (
                <>
                    {isImmediatelySettledTotalAmountPositive ? (
                        <Stack spacing="200" distribution="space-between">
                            <Highlight size="small">
                                <LocalizedText textKey={TextKey.InPropertyCurrency} />
                            </Highlight>
                            <Highlight size="small">
                                <LocalizedCurrencySimpleValue
                                    value={totalAmountInPaymentGatewayCurrency}
                                    data-test-id={DataTestId.TotalBarTotalValuePaymentGateway}
                                />
                            </Highlight>
                        </Stack>
                    ) : (
                        <Stack spacing="200" distribution="space-between">
                            <Highlight size="small">
                                <LocalizedText textKey={TextKey.InYourCurrency} />
                            </Highlight>
                            <Highlight size="small">
                                <LocalizedCurrencySimpleValue
                                    value={totalPrice}
                                    data-test-id={DataTestId.TotalBarTotalValueSelectedCurrency}
                                />
                            </Highlight>
                        </Stack>
                    )}
                </>
            ) : null}
            {hasNetPricing ? (
                <Stack alignment="end" data-test-id={DataTestId.TotalBarTaxIncluded}>
                    <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                        <LocalizedText textKey={TextKey.TaxIncluded} />
                    </Typography>
                </Stack>
            ) : null}
            {renderRateGroupDescription ? (
                <Stack alignment="end" data-test-id={DataTestId.RateGroupDescription}>
                    <Typography textStyle="bodyMediumStrong" variant="secondary" as="div">
                        {renderRateGroupDescription()}
                    </Typography>
                </Stack>
            ) : null}
            {showDccFeeMessage &&
            selectedCurrency !== paymentGatewayCurrency &&
            isImmediatelySettledTotalAmountPositive !== undefined ? (
                <Stack alignment="end" data-test-id={DataTestId.TotalBarFeeMessage}>
                    <Typography
                        textStyle="bodyMediumStrong"
                        variant="secondary"
                        as="div"
                        align="right"
                        textWrap="balance"
                    >
                        {isImmediatelySettledTotalAmountPositive ? (
                            <LocalizedText textKey={TextKey.TotalBarWithDccPreferredCurrencyInfo} />
                        ) : (
                            <LocalizedText textKey={TextKey.CardIssuerMayApplyForeignTransactionFee} />
                        )}
                    </Typography>
                </Stack>
            ) : null}
            {showSurchargingMessage && hasSurchargeService ? (
                <Body size="small" variant="secondary" data-test-id={DataTestId.SurchargingMessage}>
                    <LocalizedText
                        textKey={TextKey.SurchargeFeeMessage}
                        substitutes={{
                            visaFee: surchargeFees.visa,
                            mastercardFee: surchargeFees.mastercard,
                            amexFee: surchargeFees.amex,
                        }}
                    />
                </Body>
            ) : null}
        </Stack>
    );
};
